import { Box, SvgIcon as MUISvgIcon, Stack, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"

import { formatRelativeDate } from "@ggs/utils"
import type { FC, FCWC, ResponsiveImageType, TagProps } from ".."
import { IconButton, ResponsiveImage, SvgIcon, Tags, WishlistButton, Link } from ".."

type FeaturedBlogPostProps = {
  title: string
  image: ResponsiveImageType
  author: string
  authorLink?: string
  date: string
  uri: string
  tags: TagProps[]
  onLike?: () => void
  onUnlike?: () => void
  liked?: boolean
}

const FeaturedBlogPost: FC<FeaturedBlogPostProps> = ({
  title,
  image,
  author,
  authorLink,
  date,
  uri,
  tags,
  onLike,
  onUnlike,
  liked,
}) => {
  return (
    <Box
      position="relative"
      pt={{ xs: 10, md: 30 }}
      pb={{ xs: 10, md: 32.5 }}
      px={{ xs: 3, md: 20 }}
      sx={(theme) => ({
        backgroundColor: theme.palette["tertiary.300"].main,
        margin: "0 auto",
      })}
    >
      <BackgroundIcon />
      <FeaturedBlogPostSection>
        <FeaturedBlogPostImage image={image} title={title} uri={uri} />
        <FeaturedBlogPostContent>
          <BlogPostTags tags={tags} onLike={onLike} onUnlike={onUnlike} liked={liked} />
          <Stack rowGap={{ xs: 6, md: 10 }} mt={{ xs: 8, md: 25 }} px={{ xs: 5, md: 10 }}>
            <BlogPostTitle title={title} />
            <Stack
              direction="row"
              py={3}
              borderTop={(theme) => `1px solid ${alpha(theme.palette["neutral.100"].main, 0.1)}`}
            >
              <BlogPostDate date={date} />
              {author && <BlogPostAuthor author={author} authorLink={authorLink} />}
            </Stack>
          </Stack>
        </FeaturedBlogPostContent>
        <FeaturedBlogPostButton title={title} uri={uri} />
      </FeaturedBlogPostSection>
    </Box>
  )
}
export default FeaturedBlogPost

/** A sub-component for the section container of the featured blog content */
const FeaturedBlogPostSection: FCWC = ({ children }) => (
  <Stack
    component="header"
    alignItems="flex-start"
    justifyContent="flex-end"
    width="100%"
    position="relative"
    height={{ xs: 500, md: 700 }}
    sx={{ isolation: "isolate" }}
  >
    {children}
  </Stack>
)

/** A sub-component to render an image for the featured blog content */
const FeaturedBlogPostImage: FC<Pick<FeaturedBlogPostProps, "image" | "title" | "uri">> = ({
  image,
  title,
  uri,
}) => {
  return (
    <>
      <Link type="overlay" href={uri} aria-label={title} sx={{ zIndex: 3 }} />
      <ResponsiveImage
        image={image}
        position="absolute"
        width="100%"
        height="100%"
        sx={{ objectFit: "cover", top: 0, bottom: 0, right: 0, left: 0 }}
        zIndex={2}
        borderRadius={1.5}
      />
      <Box
        position="absolute"
        width="100%"
        height="100%"
        sx={(theme) => ({
          background: `linear-gradient(180deg, ${alpha(
            theme.palette["neutral.900"].main,
            0
          )} 0%, ${alpha(theme.palette["neutral.900"].main, 0.4)} 100%)`,
          pointerEvents: "none",
          borderRadius: theme.spacing(1.5),
        })}
        zIndex={2}
      />
    </>
  )
}

/** A sub-component for the content of the featured blog content */
const FeaturedBlogPostContent: FCWC = ({ children }) => (
  <Box maxWidth="100%" px={{ xs: 3, md: 10 }}>
    <Stack alignItems="flex-start" pb={{ xs: 3, sm: 5, md: 10 }} position="relative" zIndex={3}>
      <Stack
        width="100%"
        maxWidth={{ md: 591 }}
        minHeight="auto"
        bgcolor={(theme) => alpha(theme.palette["secondary.800"].main, 0.8)}
        borderRadius={1.5}
        boxShadow={(theme) => `0 0 8px ${alpha(theme.palette["secondary.800"].main, 0.1)}`}
        color={(theme) => theme.palette["neutral.100"].main}
        sx={{ backdropFilter: "blur(3px)" }}
      >
        {children}
      </Stack>
    </Stack>
  </Box>
)

/** A sub-component for the tags content of the featured blog content */
const BlogPostTags: FC<Pick<FeaturedBlogPostProps, "tags" | "liked" | "onLike" | "onUnlike">> = ({
  tags,
  liked,
  onLike,
  onUnlike,
}) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    py={{ xs: 3.5, md: 4.5 }}
    px={{ xs: 5, md: 10 }}
    borderBottom={(theme) => `1px solid ${alpha(theme.palette["neutral.100"].main, 0.1)}`}
  >
    {Array.isArray(tags) && tags.length > 0 && <Tags tags={tags} columnGap={3} />}
    <WishlistButton
      liked={liked}
      onLike={onLike ? onLike : () => {}}
      onUnlike={onUnlike ? onUnlike : () => {}}
    />
  </Stack>
)

/** A sub-component for the title content of the featured blog content */
const BlogPostTitle: FC<Pick<FeaturedBlogPostProps, "title">> = ({ title }) => (
  <Box>
    <Typography
      variant="heading.xl"
      component="h2"
      color={(theme) => theme.palette["neutral.100"].main}
    >
      {title}
    </Typography>
  </Box>
)

/** A sub-component for the duration content of the featured blog content */
const BlogPostDate: FC<Pick<FeaturedBlogPostProps, "date">> = ({ date }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={3}
    py={3}
    pl={{ xs: 0, md: 5 }}
    pr={{ xs: 5, md: 10 }}
    borderRight={(theme) => `1px solid ${alpha(theme.palette["neutral.100"].main, 0.1)}`}
  >
    <SvgIcon variant="clock" size={16} />
    <Typography variant="body.sm">{formatRelativeDate(date, "MMM DD, YYYY")}</Typography>
  </Stack>
)

/** A sub-component for the price content of the featured blog content */
const BlogPostAuthor: FC<Pick<FeaturedBlogPostProps, "author" | "authorLink">> = ({
  author,
  authorLink,
}) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={3}
    flex="1 1 auto"
    py={3}
    pr={5}
    pl={{ xs: 5, md: 10 }}
  >
    <Link
      type="base"
      variant="body.sm"
      href={authorLink}
      sx={{
        color: (theme) => theme.palette["neutral.100"].main,
      }}
    >
      {`By ${author}`}
    </Link>
  </Stack>
)

/** A sub-component for the Staff profile button */
const FeaturedBlogPostButton: FC<Pick<FeaturedBlogPostProps, "title" | "uri">> = ({
  title,
  uri,
}) => {
  return (
    <Box
      sx={(theme) => ({
        "display": "flex",
        "position": "absolute",
        "bottom": 40,
        "right": 40,
        "borderRadius": "100px",
        "background": theme.palette["primary.800"].main,
        "zIndex": 2,
        "&:hover": { cursor: "pointer" },
        [theme.breakpoints.down("lg")]: {
          display: "none",
        },
      })}
    >
      <IconButton
        variant="default"
        icon={<SvgIcon variant="arrowOutward" size={20} />}
        href={uri}
        aria-label={title}
      />
    </Box>
  )
}

/** A sub-component for the svg in the bg  */
const BackgroundIcon = () => {
  return (
    <Box
      sx={(theme) => ({
        position: "absolute",
        top: 0,
        display: { xs: "none", md: "block" },
        overflow: "hidden",

        [theme.breakpoints.down(1600)]: {
          right: 920,
        },

        [theme.breakpoints.up(1600)]: {
          left: 0,
        },
      })}
    >
      <MUISvgIcon
        viewBox="0 0 663 418"
        sx={{
          width: 663,
          height: 418,
          fill: "none",
        }}
      >
        <path
          d="M-1.62173e-07 413.252C84.125 448.099 77.6391 226 198.5 226C385.897 226 347.5 413.252 503.935 375.244C652.944 339.04 506.583 -9.57436 662 1.2402"
          stroke="#F05B2A"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray="0.1 10"
        />
      </MUISvgIcon>
    </Box>
  )
}
